/**
 * 상점 광고 다이아 커맨드 — 일반 상점 고정 행(2시간 쿨타임 / 오늘의 광고 다이아) 구매 흐름 (Phaser-free).
 * DailyShopCommands의 ad 슬롯 흐름을 미러: 광고 게이트(adFree 즉시/보상형 광고) + 보상 해석/지급 + 재구매 게이트.
 *
 * 재구매 게이트(gateKind):
 * - "cooldown": @yjsgames/cooldown — product.id를 키로 cooldownSeconds 만큼 재사용 불가.
 * - "daily":    SaveData.shopDailyAdDiamondClaimed(자정 리셋, @yjsgames/daily-reset) — 하루 1회.
 *
 * 컨트롤러/서비스는 deps 콜백·getter 경유 (applySaveData 교체 stale 방지 규약).
 */
import { t } from "../../i18n/i18n.js";
import {
  SHOP_AD_PRODUCTS,
  shopAdProduct,
  shopAdRewardSpec,
  type ShopAdProduct,
} from "../../data/shopAdData.js";
import { expandedModulePool } from "../../data/moduleDefinitions.js";
import { resolveRewards, type ResolvedReward } from "../../systems/RewardResolver.js";
import type { CooldownService } from "@yjsgames/cooldown";
import type { GameContext } from "../GameContext.js";
import type { ModuleInstance } from "../../models/ModuleInstance.js";
import type { ShellPorts } from "./ports.js";
import { RewardService } from "./RewardService.js";

export interface ShopAdCommandDeps {
  ctx: GameContext;
  ports: ShellPorts;
  rewards: RewardService;
  /** 기간 쿨타임 서비스 — cooldown 게이트 상품의 재구매 판정/시작. */
  cooldown: () => CooldownService;
  /** 광고 제거 entitlement — 보유 시 광고 없이 즉시 수령 (SaveData.adFree). */
  adFree: () => boolean;
  /** 광고 처리 중 — UI가 ad 상품 버튼을 준비중 상태로 표시한다. */
  adProcessing?: () => boolean;
  /** 오늘의 광고 다이아 수령 여부 (SaveData.shopDailyAdDiamondClaimed) 읽기. */
  dailyClaimed: () => boolean;
  /** 오늘의 광고 다이아 수령 여부 마킹 (자정 리셋 시 false 복귀). */
  setDailyClaimed: (claimed: boolean) => void;
  /** 보상형 광고 Port 위임 — 시청 완료 시 onReward 콜백. */
  showRewardedAd: (onReward: () => void) => void;
  /** 안내 토스트 — 쿨타임 중/이미 수령 등. */
  showToast: (message: string) => void;
  /** 보상 획득 모달 — 다이아 지급 결과 표시. */
  openRewardModal: (reward: ResolvedReward, instances?: readonly ModuleInstance[]) => void;
}

export class ShopAdCommands {
  constructor(private readonly deps: ShopAdCommandDeps) {}

  adProcessing(): boolean {
    return this.deps.adProcessing?.() ?? false;
  }

  /** 자정 리셋 — 오늘의 광고 다이아 수령 플래그 복귀(다시 1회 수령 가능). dailyResets 콜백에서 호출. */
  resetDailyClaimed(): void {
    this.deps.setDailyClaimed(false);
  }

  /** 치트/QA — 모든 광고 다이아 재구매 게이트 해제(쿨타임 clear + 오늘 수령 플래그 복귀). */
  resetGates(): void {
    for (const p of SHOP_AD_PRODUCTS) {
      if (p.gateKind === "cooldown") this.deps.cooldown().clear(p.id);
    }
    this.deps.setDailyClaimed(false);
  }

  /** 게이트 활성 여부 — UI(버튼 disable/카운트다운)와 buy 진입이 공용으로 쓰는 순수 판정. */
  isGated(productId: string): boolean {
    const product = shopAdProduct(productId);
    if (!product) return true;
    return product.gateKind === "cooldown"
      ? this.deps.cooldown().isActive(product.id)
      : this.deps.dailyClaimed();
  }

  /** 광고 다이아 구매 — 게이트 통과 시 광고 게이트(adFree 즉시/보상형 광고) → 지급. */
  buyAdDiamond(productId: string): void {
    const product = shopAdProduct(productId);
    if (!product) return;
    if (this.adProcessing()) return;
    if (this.isGated(productId)) {
      const msg =
        product.gateKind === "cooldown"
          ? t("shop.adDiamond.cooldownActive")
          : t("shop.adDiamond.alreadyClaimed");
      this.deps.showToast(msg);
      this.deps.ports.invalidateUi();
      return;
    }
    // 광고 제거(adFree) 보유 시 광고 없이 즉시 수령, 미보유 시 보상형 광고(Port) 시청 완료 후 지급(DIP).
    if (this.deps.adFree()) {
      this.fulfill(product);
    } else {
      this.deps.showRewardedAd(() => this.fulfill(product));
    }
  }

  /** 보상 지급 + 재구매 게이트 마킹 — adFree 즉시/광고 시청 완료 후 공통. */
  private fulfill(product: ShopAdProduct): void {
    const { ctx } = this.deps;
    ctx.soundController().play("box");
    const spec = shopAdRewardSpec(product);
    const reward = resolveRewards([spec], {
      rng: Math.random,
      rarityDefs: expandedModulePool(),
    });
    const insts = this.deps.rewards.grant(reward);
    this.deps.openRewardModal(reward, insts);
    // 재구매 게이트 시작 — cooldown은 product.id 키로 쿨타임 시작, daily는 오늘 수령 마킹.
    if (product.gateKind === "cooldown") {
      this.deps.cooldown().start(product.id, product.cooldownSeconds * 1000);
    } else {
      this.deps.setDailyClaimed(true);
    }
    this.deps.ports.invalidateUi();
    this.deps.ports.persistNow(); // 광고 보상 수령 — 즉시 저장(유실 불가)
  }
}
